import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { eventService } from '@/lib/api/event.service';
import { guestService } from '@/lib/api/guest.service';
import { celebrationService } from '@/lib/api/celebration.service';
import { groupService } from '@/lib/api/group.service';
import { teamMemberService } from '@/lib/api/teamMember.service';
import { useAuth } from '@/hooks/useAuth';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { PageState } from '@/components/layout/PageState';
import { HeroQuickMenuCard } from '@/components/shared/HeroQuickMenuCard';
import { Button } from '@/components/ui/button';
import type { Event } from '@/types/event.types';
import type { GuestGroup, GroupMembership } from '@/types/group.types';
import type { TeamRole, TeamMemberWithUser } from '@/types/teamMember.types';
import { Users, CalendarDays, LayoutGrid, UserPlus } from 'lucide-react';
import { GuestListTab } from '@/components/guests/GuestListTab';
import { EventsTab } from '@/components/guests/EventsTab';
import { GroupsTab } from '@/components/guests/GroupsTab';
import { AddCelebrationGuestsPanel } from '@/components/guests/AddCelebrationGuestsPanel';
import type { GuestRow } from '@/components/guests/GuestListTab';
import { buildCelebrationGuestRows } from '@/components/guests/guestRows';
import { canAddRemoveGuests } from '@/utils/permissions';

type TabId = 'guests' | 'events' | 'groups';

interface HostFilterOption {
  userId: number;
  label: string;
}

export const CelebrationGuestsGrid: React.FC = () => {
  const { celebrationId } = useParams<{ celebrationId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation(['guests', 'common', 'invitations']);
  const [activeTab, setActiveTab] = useState<TabId>('guests');
  const [events, setEvents] = useState<Event[]>([]);
  const [guestRows, setGuestRows] = useState<GuestRow[]>([]);
  const [originalGuestRows, setOriginalGuestRows] = useState<GuestRow[]>([]);
  const [groups, setGroups] = useState<GuestGroup[]>([]);
  const [memberships, setMemberships] = useState<GroupMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [celebrationName, setCelebrationName] = useState('');
  const [pendingGroupFilter, setPendingGroupFilter] = useState<number | null>(null);
  const [currentRole, setCurrentRole] = useState<TeamRole | null>(null);
  const [selectedHostUserId, setSelectedHostUserId] = useState<number | null>(null);
  const [hostFilterOptions, setHostFilterOptions] = useState<HostFilterOption[]>([]);
  const [isAddGuestsPanelOpen, setIsAddGuestsPanelOpen] = useState(false);

  const loadData = useCallback(async (options?: { silent?: boolean }) => {
    if (!celebrationId) return;
    try {
      // Silent refreshes keep modals mounted by skipping the full-page loading state.
      if (!options?.silent) setLoading(true);
      setError(null);

      // Load celebration details
      const celebration = await celebrationService.getCelebration(Number(celebrationId));
      setCelebrationName(celebration.title);

      // Load events, guests, groups, memberships, and team members in parallel
      // getMemberships is non-critical — don't let it block guest display
      const [allEvents, guestDirectory, groupsData, membershipsData, teamMembers] = await Promise.all([
        eventService.getEvents(),
        guestService.getCelebrationGuestDirectory(Number(celebrationId)),
        groupService.getGroups(Number(celebrationId)),
        groupService.getMemberships(Number(celebrationId)).catch(() => [] as GroupMembership[]),
        teamMemberService.getTeamMembers(Number(celebrationId)).catch(() => [] as TeamMemberWithUser[]),
      ]);
      const allGuests = guestDirectory.guests;

      const activeMembers = teamMembers.filter((member) => member.status === 'active');
      const currentMember = activeMembers.find((member) => member.user_id === user?.id);
      setCurrentRole(currentMember?.role || null);

      const hostOptions: HostFilterOption[] = activeMembers
        .filter((member) => canAddRemoveGuests(member.role))
        .map((member) => ({
          userId: member.user_id,
          label: member.user.full_name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setHostFilterOptions(hostOptions);

      const celebrationEvents = allEvents.filter(
        (e) => e.celebration_id === Number(celebrationId) && !e.deleted_at
      );
      setEvents(celebrationEvents);
      setGroups(groupsData);
      setMemberships(membershipsData);

      const sortedRows = buildCelebrationGuestRows(allGuests);
      setGuestRows(sortedRows);
      setOriginalGuestRows(sortedRows.map(r => ({ ...r, eventGuests: new Map(r.eventGuests) })));
    } catch (err) {
      console.error('Error loading celebration guests:', err);
      setError(t('guests:grid.error.message'));
    } finally {
      setLoading(false);
    }
  }, [celebrationId, user?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (guestRows.length === 0 && activeTab !== 'guests') setActiveTab('guests');
  }, [guestRows.length, activeTab]);

  useEffect(() => {
    if (currentRole !== 'organizer' && selectedHostUserId !== null) {
      setSelectedHostUserId(null);
    }
  }, [currentRole, selectedHostUserId]);

  const handleDataChanged = () => {
    loadData();
  };

  const handleGroupFilterConsumed = () => {
    setPendingGroupFilter(null);
  };

  if (loading) {
    return (
      <PageState
        kind="loading"
        width="wide"
        title={t('guests:grid.loading.title')}
        description={t('guests:grid.loading.description')}
      />
    );
  }

  if (error) {
    return (
      <PageState
        kind="error"
        width="wide"
        title={t('guests:grid.error.title')}
        description={error}
        action={
          <Button
            size="sm"
            onClick={() => loadData()}
            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            {t('common:actions.retry')}
          </Button>
        }
      />
    );
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <AppBreadcrumbs
        items={[
          { label: 'Home', to: '/home' },
          { label: 'Celebrations', to: '/celebrations' },
          { label: celebrationName || 'Celebration', to: `/celebrations/${celebrationId}` },
          { label: 'Guests' },
        ]}
      />

      <HeroQuickMenuCard
        borderClassName="border-primary/20"
        heroClassName="bg-gradient-to-br from-primary/10 via-primary/[0.04] to-white"
        heroContent={
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center flex-shrink-0">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight text-foreground">{t('guests:title')}</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('guests:grid.subtitle')}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={() => setIsAddGuestsPanelOpen(true)}>
                <UserPlus className="h-4 w-4 mr-1" />
                Add guests
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate(`/celebrations/${celebrationId}/invitations`)}>
                {t('guests:grid.buttons.invitations')}
              </Button>
            </div>
          </div>
        }
        columns={3}
        items={[
          {
            key: 'guest-list',
            icon: Users,
            iconClassName: 'text-blue-600',
            hoverClassName: 'hover:bg-blue-50',
            active: activeTab === 'guests',
            label: t('guests:grid.tabs.guestList'),
            value: guestRows.length,
            onClick: () => setActiveTab('guests'),
          },
          {
            key: 'events',
            icon: CalendarDays,
            iconClassName: 'text-amber-600',
            hoverClassName: 'hover:bg-amber-50',
            active: activeTab === 'events',
            label: t('guests:grid.tabs.eventAssignment'),
            value: null,
            disabled: guestRows.length === 0,
            onClick: () => setActiveTab('events'),
          },
          {
            key: 'groups',
            icon: LayoutGrid,
            iconClassName: 'text-violet-600',
            hoverClassName: 'hover:bg-violet-50',
            active: activeTab === 'groups',
            label: t('guests:grid.tabs.groups'),
            value: groups.length,
            disabled: guestRows.length === 0,
            onClick: () => setActiveTab('groups'),
          },
        ]}
      />

      {/* Tab Content */}
      {activeTab === 'guests' && (
        <GuestListTab
          events={events}
          guestRows={guestRows}
          groups={groups}
          memberships={memberships}
          loading={loading}
          showHostFilter={currentRole === 'organizer'}
          hostFilterOptions={hostFilterOptions}
          selectedHostUserId={selectedHostUserId}
          onHostFilterChange={setSelectedHostUserId}
          initialGroupFilter={pendingGroupFilter}
          onGroupFilterConsumed={handleGroupFilterConsumed}
          onAddGuests={() => setIsAddGuestsPanelOpen(true)}
        />
      )}

      {activeTab === 'events' && (
        <EventsTab
          celebrationId={Number(celebrationId)}
          events={events}
          guestRows={guestRows}
          originalGuestRows={originalGuestRows}
          groups={groups}
          actorRole={currentRole}
          onDataChanged={handleDataChanged}
          onAddGuests={() => setIsAddGuestsPanelOpen(true)}
        />
      )}

      {activeTab === 'groups' && (
        <GroupsTab
          celebrationId={Number(celebrationId)}
          events={events}
          guestRows={guestRows}
          groups={groups}
          memberships={memberships}
          onGroupsChanged={handleDataChanged}
          onAddGuests={() => setIsAddGuestsPanelOpen(true)}
        />
      )}

      {isAddGuestsPanelOpen && celebrationId && (
        <AddCelebrationGuestsPanel
          celebrationId={Number(celebrationId)}
          celebrationName={celebrationName}
          onClose={() => setIsAddGuestsPanelOpen(false)}
          onAdded={() => loadData({ silent: true })}
          onReviewEventAssignments={() => setActiveTab('events')}
        />
      )}
    </PageContainer>
  );
};
